/* eslint-disable no-console */
import { enableProdMode, NgModuleRef } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import { SignService } from './app/services/sign.service';

import * as Sentry from '@sentry/angular-ivy';
import { LoggingService } from './app/services/logging.service';
import 'hammerjs';

if (environment.production) {
  enableProdMode();
}

function initSentry(): void {
  Sentry.init({
    dsn: environment.SENTRY_DSN,
    transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
    transportOptions: {
      maxQueueSize: 100,
    },
    /*integrations: [
      // Registers and configures the Tracing integration,
      // which automatically instruments your application to monitor its
      // performance, including custom Angular routing instrumentation.
      new BrowserTracing({
        tracePropagationTargets: ['localhost', environment.API_URL],
        routingInstrumentation: Sentry.routingInstrumentation,
      }),
      new Sentry.Replay(),
    ],*/
    environment: environment.sentry_env,
    // release: environment.version,
    ignoreErrors: ['Non-Error exception captured'],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0,
    enabled: true,
    // This sets the sample rate to be 50%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    // replaysSessionSampleRate: 0.5,
    // If the entire session is not sampled, use the below sample rate to sample
    // sessions when an error occurs.
    // replaysOnErrorSampleRate: 1.0,
  });
}

initSentry();

function updateOnlineStatus(): void {
  if (navigator.serviceWorker?.controller) {
    navigator.serviceWorker.controller.postMessage({ type: 'ONLINE_STATUS' }, []);
  }
}

function initCommunicationChannel(ref: NgModuleRef<AppModule>): MessageChannel | null {
  const signService = ref.injector.get(SignService);
  const logger = ref.injector.get(LoggingService);

  const messageChannel = new MessageChannel();
  if (navigator.serviceWorker?.controller) {
    navigator.serviceWorker.controller.postMessage(
      { type: environment.production ? 'PORT_INITIALIZATION_PROD' : 'PORT_INITIALIZATION' },
      [messageChannel.port2],
    );
    // listen on the provided port for messages from service worker
    messageChannel.port1.onmessage = (event): void => {
      logger.info(event);
      const message = JSON.parse(event.data);
      if (message.type === 'REQUEST') {
        // the service worker sent a message to sign the request
        signService
          .signRequest(message.request.url, message.request.method, message.request.body)
          .then((signedParams) => {
            if (navigator.serviceWorker && navigator.serviceWorker.controller) {
              navigator.serviceWorker.controller.postMessage(
                {
                  type: 'SIGNED_PARAMS',
                  signedParams: signedParams,
                  key: message.key,
                },
                [],
              );
              logger.debug('sent SIGNED_PARAMS to service worker');
            }
          })
          .catch((error) => {
            logger.error(error);
          });
      } else if (message.type === 'RESPONSE') {
        // the service worker sent a message with the response from a call it did
        if (
          message.error ||
          (message.response &&
            message.response.status !== 200 &&
            message.response &&
            message.response.status !== 202 &&
            message.response.status !== 204)
        ) {
          const errorMessage: any = message.error
            ? message.error
            : message.response
              ? JSON.stringify(message.response)
              : 'Unknown error when synchronizing offline work';
          logger.error(errorMessage);
        } else {
          // do not add the request body which can be quite big in the message
          if (message.request?.body) {
            delete message.request.body;
          }
        }
      }
    };
    logger.info('Service worker message channel installed');
    return messageChannel;
  } else {
    logger.error(
      'Could not send message channel to service worker because navigator.serviceWorker?.controller is not defined',
      false,
    );
    return null;
  }
}

/*
  Most of the offline work is done by the service worker. By default it caches GET
  requests but not POST, PUT, PATCH and DELETE. We created a custom service worker (see custom-service-worker.js)
  to intercept  POST, PUT, PATCH and DELETE calls when offline and stores them in IndexDB. When returning
  online, it sends the requests but signs them again because the original signature is
  probably expired. There is a back and forth communication between the Angular application (here)
  and the service worker in order to sign the requests using Amplify (see sign-service.ts)
 */
platformBrowserDynamic()
  .bootstrapModule(AppModule)
  .then((ref) => {
    const logger = ref.injector.get(LoggingService);
    if ('serviceWorker' in navigator) {
      if (environment.production) {
        window.addEventListener('load', () => {
          navigator.serviceWorker
            .register('custom-service-worker.js', { scope: './' })
            .then((sw) => {
              logger.info('service worker installed', sw);
            })
            .catch((error) => {
              logger.error('service worker register error', error);
            });
        });
        navigator.serviceWorker.ready
          .then(function (registration) {
            logger.info('service worker ready');
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            return registration.sync?.register('sendPostData');
          })
          .then(function () {
            logger.info('service worker sync event registered');
            if (!initCommunicationChannel(ref)) {
              initCommunicationChannel(ref);
            }
            window.addEventListener('online', updateOnlineStatus);
            window.addEventListener('offline', updateOnlineStatus);
          })
          .catch(function (error) {
            // system was unable to register for a sync,
            // this could be an OS-level restriction
            logger.error(error);
          });
      } else {
        logger.warn('service worker not installed in non prod deployments');
      }
    } else {
      logger.error('service worker not supported in this browser', false);
    }
  })
  .catch((error) => console.error('bootstrapModule error: ', error));
